import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode, useState, useEffect } from 'react';
import { Container, Spinner } from 'react-bootstrap';

import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { localStore } from '../../../../v2/js/utils/storage';
import { ApiError, AuthService, UserTypeEnum } from '../../../api';
import { setSessionHandler } from '../../../api/core/request';
import { clearBrowserCookies, getCookieValue } from '../../../utils/cookies';
import { SessionContext } from '../../contexts/SessionContext';
import { useCurrentUser, useCurrentUserNotifications } from '../../hooks/auth';
import { handleApiError } from '../../utils/errorhandling';
import { identifyUser } from '../../utils/logrocket';

interface Props {
  children?: ReactNode;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function Session({ children }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Set up session expiration handler
  useEffect(() => {
    setSessionHandler({
      handleSessionExpired: (isAuthError: boolean = false) => {
        // Clear all session data
        queryClient.cancelQueries();
        queryClient.clear();
        clearBrowserCookies();
        setSessionExists(false);
        setInitialCheckComplete(true);
        Sentry.setUser(null);
        console.log('Session expired, redirecting to login page');

        // Don't create nested redirects if we're already on login page
        if (location.pathname.startsWith('/login')) {
          console.log('Already on login page, not redirecting');
          return;
        }

        // Build the login URL with next parameter and session expired flag
        let loginUrl: string;
        const sessionExpiredParam = isAuthError ? 'session_expired=true' : '';
        if (!isSessionEstablishmentExempt) {
          const currentPath = location.pathname + location.search;
          const nextParam = encodeURIComponent(currentPath);
          loginUrl = `/login?next=${nextParam}&${sessionExpiredParam}`;
        } else {
          loginUrl = `/login?${sessionExpiredParam}`;
        }

        navigate(loginUrl, { replace: true });
      },
    });
  }, [navigate, queryClient, location.pathname, location.search]);

  // Don't assume session exists if we're on a social login callback route
  const isOAuthCallback =
    location.pathname.includes('/social-login/') &&
    (location.search.includes('code=') || location.search.includes('state='));

  // Pages that should not attempt session establishment
  const isSessionEstablishmentExempt =
    location.pathname.startsWith('/invitation') ||
    location.pathname.startsWith('/register/provider/setup') ||
    location.pathname.startsWith('/register/provider/sourced') ||
    location.pathname.startsWith('/password-reset') ||
    location.pathname.includes('/password-reset/') ||
    location.pathname.startsWith('/verify-email') ||
    location.pathname === '/forgot-password' ||
    location.pathname === '/reset-password' ||
    location.pathname.startsWith('/swag') ||
    location.pathname.startsWith('/endorsement');

  // Pages that allow optional authentication (can be accessed anonymously, but will fetch user if logged in)
  const isOptionalAuthRoute = location.pathname.startsWith('/pub');

  const isRootRoute: boolean = location.pathname === '/';

  // Don't check for session on exempt routes (but allow optional auth routes)
  const shouldSkipSessionCheck = isOAuthCallback || isSessionEstablishmentExempt;

  // Check if there's actually a session cookie before trying to fetch user data
  const hasSessionIndicator = !!getCookieValue('csrftoken');

  // For login page, we want to check for existing session if cookie exists
  const isLoginPage = location.pathname.startsWith('/login');

  // Initialize session state
  // For optional auth routes, set sessionExists if we have a cookie (but don't require it)
  const [sessionExists, setSessionExists] = useState(
    isOptionalAuthRoute
      ? hasSessionIndicator
      : !shouldSkipSessionCheck && (hasSessionIndicator || (!isLoginPage && !isRootRoute))
  );

  // Initial check is complete for exempt routes or when no session cookie exists on login page
  // For optional auth routes, we're done if no cookie exists, otherwise wait for user fetch
  const [initialCheckComplete, setInitialCheckComplete] = useState(
    isOptionalAuthRoute
      ? !hasSessionIndicator
      : shouldSkipSessionCheck || ((isLoginPage || isRootRoute) && !hasSessionIndicator)
  );

  // Determine when to attempt user fetch
  // For optional auth routes, attempt fetch if we have a session cookie
  // For other routes, use the existing logic
  const shouldAttemptUserFetch = isOptionalAuthRoute
    ? hasSessionIndicator
    : sessionExists && !shouldSkipSessionCheck && (hasSessionIndicator || !isLoginPage);

  const currentUserData = useCurrentUser({
    enabled: shouldAttemptUserFetch,
    onSuccess: (userData) => {
      // Set Sentry user on successful auth
      if (userData) {
        Sentry.setUser({
          id: userData.id,
          email: userData.email,
          user_type: userData.user_type,
        });
        identifyUser(userData.id, {
          name: userData.first_name + ' ' + userData.last_name,
          email: userData.email,
        });
        // techdebt: seems to be used for resend verification email... not a good solution
        localStore.set('authLastSigninEmail', userData.email);
      }
      setInitialCheckComplete(true);
    },
    onError: (error) => {
      // For optional auth routes, don't show error if user fetch fails (user might not be logged in)
      if (!isOptionalAuthRoute) {
        handleApiError(error);
      }
      setInitialCheckComplete(true);
    },
  });

  const currentUserNotifications = useCurrentUserNotifications({
    enabled:
      (sessionExists || isOptionalAuthRoute) &&
      !currentUserData.isLoading &&
      !currentUserData.errors &&
      !shouldSkipSessionCheck,
  });

  const mutationLogout = useMutation({
    mutationFn: async () => {
      await AuthService.authLogoutCreate();
    },
    onSuccess: () => {
      queryClient.clear();
      clearBrowserCookies();
      setSessionExists(false);
      currentUserData.remove();
      navigate('/');
    },
    onError: (errors: ApiError) => {
      toast.error(errors.body?.detail || "Couldn't logout");
      // Even if logout fails on server, clear client state
      queryClient.clear();
      clearBrowserCookies();
      setSessionExists(false);
      currentUserData.remove();
      navigate('/');
    },
  });

  // Handle session establishment (e.g., after successful login)
  const handleSessionEstablished = () => {
    setSessionExists(true);
    setInitialCheckComplete(false);
    // Only refetch if we don't already have user data
    if (!currentUserData?.user) {
      currentUserData.refetch();
    }
  };

  const shouldShowLoading =
    !initialCheckComplete ||
    ((sessionExists || (isOptionalAuthRoute && hasSessionIndicator)) &&
      currentUserData.isLoading &&
      !shouldSkipSessionCheck);

  // Show loading spinner while checking session or loading user data
  if (shouldShowLoading) {
    return (
      // techdebt: change to tailwind
      <Container
        className="content-wrapper justify-content-center align-items-center text-center"
        fluid
      >
        <Spinner
          animation="grow"
          aria-label="Loading"
          className="loading-spinner tw:mt-8"
          variant="primary"
        />
      </Container>
    );
  }

  return (
    <SessionContext.Provider
      value={{
        ...currentUserData,
        sessionExists,
        setSessionExists: handleSessionEstablished,
        notifications: currentUserNotifications,
        logout: () => mutationLogout.mutateAsync(),
        currentEntity: currentUserData.user?.healthcare_entity_name,
        audience:
          currentUserData.user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP
            ? 'healthcare_entity'
            : currentUserData.user?.user_type,
        audienceIsProvider: currentUserData.user?.user_type === UserTypeEnum.PROVIDER,
        audienceIsOrg: currentUserData.user?.user_type === UserTypeEnum.HEALTHCARE_ENTITY_REP,
        audienceIsStaff: currentUserData.user?.user_type === UserTypeEnum.STAFF,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
}
